import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import { getCSS, getCSSVariableValue } from '../../../../../../_metronic/assets/ts/_utils'
import { printShortDate } from '../../../../../../utils/dateHelpers'
import { formatVerificationChecksKyb } from '../../../../../../utils/streamlineSpecificHelpers'

type Props = {
  data: any,
  className: string,
  activeType: string
}

const TransferTotalChart: React.FC<Props> = ({className, data, activeType}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))
    const chart = new ApexCharts(chartRef.current, getChartOptions(height, data))

    if (chart) {
      chart.render()
    }

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [chartRef, data])

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-3 mb-1'>Selected Type:
            <span className='fs-5 text-uppercase px-2 text-gray-600'>
              {activeType ? formatVerificationChecksKyb(activeType).title : 'NONE'}
            </span>
          </span>
          <span className='text-muted fw-bold fs-7'>Successful Vs failed verifications by date performed</span>
        </h3>
      </div>
      <div className='card-body'>
        <div ref={chartRef} id='kt_charts_widget_2_chart' style={{height: '400px'}}></div>
      </div>
    </div>
  )
}

export {TransferTotalChart}

function getChartOptions(height: number, data:any): ApexOptions {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-success')
  const secondaryColor = getCSSVariableValue('--bs-danger')

  const SUCCESSFUL:any =  data?.map((i:any) => {
    return i?.success_count
  }) || []

  const FAILED:any =  data?.map((i:any) => {
    return i?.failed_count
  }) || []

  const DAYS:any =  data?.map((i:any) => {
    return printShortDate(i?.performed_date)
  }) || []

  return {
    series: [
      {
        name: 'Successful',
        data: SUCCESSFUL?.reverse(),
      },
      {
        name: 'Failed',
        data: FAILED?.reverse(),
      }
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: height,
      toolbar: {
        show: false,
      },
      stacked: false
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '35%',
        borderRadius: 4,
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: DAYS?.reverse(),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    fill: {
      opacity: 1,
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          return '' + val + ''
        },
      },
    },
    colors: [baseColor, secondaryColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
  }
}
